// src/components/issues/tableau/TableauProblemes.tsx — SCE Issues
// Le tableau de « Tous les problèmes » au bureau (≥ 768 px). Grille CSS à
// gabarit explicite plutôt qu'une <table> : les colonnes sont décidées par les
// réglages et par la place, pas par le contenu de la ligne la plus longue.
//
// - En-têtes cliquables : tri (Maj = tri secondaire), entonnoir = filtre,
//   bord droit = largeur à la main (double-clic = automatique).
// - Titres de groupe : le titre ouvre la fiche (machine, projet) ou replie
//   (métier, statut) ; le chevron replie toujours.
// - Le tableau défile en lui-même : l'en-tête reste visible, la page ne bouge pas.

import { useRef, useState, type KeyboardEvent, type PointerEvent as PointerReact } from 'react'
import { ArrowDown, ArrowUp, ArrowUpRight, ChevronDown, ChevronRight, ChevronsUpDown, Filter } from 'lucide-react'
import { CriticiteLibellee, StatutLibelle } from '@/components/ui/EtatProbleme'
import { useOuvrirFiche } from '@/components/layout/ModaleRoute'
import { METIERS_COULEURS, METIERS_LABELS_COURT } from '@/config/metiers.config'
import { useLargeurElement } from '@/hooks/useLargeurElement'
import { useLargeursAuto } from '@/hooks/useLargeursAuto'
import type { Issue } from '@/types/issue.types'
import { lienEquipement, lienProbleme, lienProjet } from '@/utils/liensFiches'
import {
  DEFINITIONS_COLONNES,
  SANS_VALEUR,
  colonnesAffichees,
  gabaritColonnes,
  moinsAvancee,
  nbValeursFiltrees,
  repartirLargeurs,
  resumerGroupe,
  trierPar,
  valeurCommune,
  type ColonneId,
  type ContexteTableau,
  type EntreeTableau,
  type Regroupement,
  type ReglagesTableau,
} from '@/utils/tableauProblemes'
import { libelleMachines } from '@/utils/groupes'
import { EtiquettesSoeurs } from '../EtiquettesSoeurs'
import { ContenuCellule, PastilleMetier } from './valeurs'

export interface TableauProblemesProps {
  entrees: readonly EntreeTableau[]
  /** Tous les problèmes chargés : les largeurs automatiques se mesurent sur eux. */
  tous: readonly Issue[]
  reglages: ReglagesTableau
  modifier: (changer: (prev: ReglagesTableau) => ReglagesTableau) => void
  ctx: ContexteTableau
  interne: boolean
  repliees: ReadonlySet<string>
  onBasculerGroupe: (cle: string) => void
  /** Colonne dont le filtre est ouvert, pour griser son entonnoir. */
  filtreOuvert: ColonneId | null
  onOuvrirFiltre: (col: ColonneId, ancre: HTMLElement) => void
  onEffacerFiltres: () => void
}

export function TableauProblemes({
  entrees,
  tous,
  reglages,
  modifier,
  ctx,
  interne,
  repliees,
  onBasculerGroupe,
  filtreOuvert,
  onOuvrirFiltre,
  onEffacerFiltres,
}: TableauProblemesProps): JSX.Element {
  const defilRef = useRef<HTMLDivElement>(null)
  const place = useLargeurElement(defilRef)
  const auto = useLargeursAuto(tous, ctx)
  // Largeur en cours de glissement : affichée tout de suite, enregistrée au lâcher.
  const [enCours, setEnCours] = useState<{ col: ColonneId; largeur: number } | null>(null)

  const colonnes = colonnesAffichees(reglages, interne)
  const manuelles = enCours ? { ...reglages.largeurs, [enCours.col]: enCours.largeur } : reglages.largeurs
  const { largeurs, total } = repartirLargeurs(colonnes, auto, manuelles, place)
  const ouvrirFiche = useOuvrirFiche()

  function demarrerRedimensionnement(e: PointerReact<HTMLElement>, col: ColonneId): void {
    e.preventDefault()
    e.stopPropagation()
    const poignee = e.currentTarget
    poignee.setPointerCapture(e.pointerId)
    const departX = e.clientX
    const departL = poignee.parentElement?.getBoundingClientRect().width ?? DEFINITIONS_COLONNES[col].min
    const min = DEFINITIONS_COLONNES[col].min
    let derniere = departL
    let bouge = false
    function deplacer(ev: PointerEvent): void {
      bouge = true
      derniere = Math.round(Math.min(600, Math.max(min, departL + ev.clientX - departX)))
      setEnCours({ col, largeur: derniere })
    }
    function finir(): void {
      poignee.removeEventListener('pointermove', deplacer)
      poignee.removeEventListener('pointerup', finir)
      poignee.removeEventListener('pointercancel', finir)
      setEnCours(null)
      if (bouge) modifier((r) => ({ ...r, largeurs: { ...r.largeurs, [col]: derniere } }))
    }
    poignee.addEventListener('pointermove', deplacer)
    poignee.addEventListener('pointerup', finir)
    poignee.addEventListener('pointercancel', finir)
  }

  function ouvrirAuClavier(e: KeyboardEvent, action: () => void): void {
    if (e.target !== e.currentTarget) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      action()
    }
  }

  return (
    <div
      ref={defilRef}
      className={`tableau-defile tableau-defile--${reglages.densite}`}
      role="table"
      aria-label="Problèmes"
      aria-rowcount={entrees.length}
      style={
        {
          '--tableau-cols': gabaritColonnes(colonnes, largeurs),
          '--tableau-min': `${total}px`,
        } as React.CSSProperties
      }
    >
      {/* ─── En-tête ─── */}
      <div className="tableau-ligne tableau-ligne--entete" role="row">
        <span aria-hidden="true" />
        {colonnes.map((col) => {
          const def = DEFINITIONS_COLONNES[col]
          const rang = reglages.tri.findIndex((t) => t.col === col)
          const critere = rang >= 0 ? reglages.tri[rang]! : null
          const nbChoisies = nbValeursFiltrees(reglages, col)
          return (
            <div
              key={col}
              className={'tableau-entete' + (def.droite ? ' tableau-entete--droite' : '')}
              role="columnheader"
              aria-sort={critere ? (critere.sens === 'asc' ? 'ascending' : 'descending') : 'none'}
            >
              <button
                type="button"
                className={'tableau-tri' + (critere ? ' tableau-tri--actif' : '')}
                title="Trier · Maj + clic : tri secondaire"
                onClick={(e) => modifier((r) => ({ ...r, tri: trierPar(r.tri, col, e.shiftKey) }))}
              >
                <span className="tableau-tri-libelle">{def.libelle}</span>
                <span className="tableau-tri-indice" aria-hidden="true">
                  {critere ? (
                    <>
                      {critere.sens === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                      {reglages.tri.length > 1 && <b>{rang + 1}</b>}
                    </>
                  ) : (
                    <ChevronsUpDown size={14} />
                  )}
                </span>
              </button>
              {def.filtrable && (
                <button
                  type="button"
                  className={
                    'tableau-filtre' +
                    (nbChoisies > 0 ? ' tableau-filtre--actif' : '') +
                    (filtreOuvert === col ? ' tableau-filtre--ouvert' : '')
                  }
                  aria-label={`Filtrer ${def.libelle}`}
                  aria-haspopup="dialog"
                  onClick={(e) => onOuvrirFiltre(col, e.currentTarget)}
                >
                  <Filter size={14} />
                  {nbChoisies > 0 && <b>{nbChoisies}</b>}
                </button>
              )}
              {col !== 'titre' && (
                <span
                  className="tableau-poignee"
                  title="Tirer pour fixer la largeur · double-clic : largeur automatique"
                  onPointerDown={(e) => demarrerRedimensionnement(e, col)}
                  onDoubleClick={() =>
                    modifier((r) => {
                      const { [col]: _retire, ...reste } = r.largeurs
                      void _retire
                      return { ...r, largeurs: reste }
                    })
                  }
                />
              )}
            </div>
          )
        })}
      </div>

      {/* ─── Lignes ─── */}
      {entrees.length === 0 && (
        <div className="tableau-vide">
          Aucun résultat pour ces critères.
          <button type="button" onClick={onEffacerFiltres}>
            Effacer les filtres
          </button>
        </div>
      )}
      {entrees.map((entree) => {
        if (entree.type === 'groupe') {
          return (
            <LigneGroupe
              key={`g:${entree.cle}`}
              mode={reglages.regroupement!}
              cle={entree.cle}
              items={entree.items}
              ctx={ctx}
              replie={repliees.has(entree.cle)}
              onBascule={() => onBasculerGroupe(entree.cle)}
              onOuvrir={(chemin) => ouvrirFiche(chemin)}
            />
          )
        }
        const { issue, soeurs } = entree
        const ouvrir = (): void => ouvrirFiche(lienProbleme(issue.id))
        return (
          <div
            key={issue.id}
            className={'tableau-ligne tableau-ligne--donnee' + (soeurs ? ' tableau-ligne--soeurs' : '')}
            role="row"
            tabIndex={0}
            onClick={ouvrir}
            onKeyDown={(e) => ouvrirAuClavier(e, ouvrir)}
          >
            {/* Liseré métier. La couleur seule ne dit rien à un lecteur d'écran :
                le métier est donc aussi écrit (infobulle et texte masqué). */}
            <span
              className="tableau-lisere"
              style={{ backgroundColor: METIERS_COULEURS[issue.metier] }}
              title={METIERS_LABELS_COURT[issue.metier]}
            >
              <span className="sr-only">{METIERS_LABELS_COURT[issue.metier]}</span>
            </span>
            {colonnes.map((col) => (
              <div
                key={col}
                role="cell"
                className={'tableau-cellule' + (DEFINITIONS_COLONNES[col].droite ? ' tableau-cellule--droite' : '')}
              >
                {soeurs ? (
                  <CelluleSoeurs
                    col={col}
                    issue={issue}
                    soeurs={soeurs}
                    ctx={ctx}
                    extrait={reglages.extrait}
                    onOuvrir={(s) => ouvrirFiche(lienProbleme(s.id))}
                  />
                ) : (
                  <ContenuCellule col={col} issue={issue} ctx={ctx} extrait={reglages.extrait} />
                )}
              </div>
            ))}
          </div>
        )
      })}
    </div>
  )
}

// ─── Rangée d'un défaut sur plusieurs équipements ───

/**
 * Titre une fois et une étiquette par équipement (état et badge de chacun) ;
 * État = la fiche la moins avancée ; Badge vide (il est dans l'étiquette de
 * l'équipement concerné) ; ailleurs, la valeur si toutes les fiches la partagent.
 */
function CelluleSoeurs({
  col,
  issue,
  soeurs,
  ctx,
  extrait,
  onOuvrir,
}: {
  col: ColonneId
  issue: Issue
  soeurs: readonly Issue[]
  ctx: ContexteTableau
  extrait: boolean
  onOuvrir: (issue: Issue) => void
}): JSX.Element | null {
  switch (col) {
    case 'id':
      return <ContenuCellule col={col} issue={issue} ctx={ctx} />
    case 'titre':
      return (
        <span className="tableau-soeurs-titre">
          <ContenuCellule col={col} issue={issue} ctx={ctx} extrait={extrait} />
          <EtiquettesSoeurs soeurs={soeurs} onOuvrir={onOuvrir} />
        </span>
      )
    case 'badge':
      return null
    case 'etat':
    case 'criticite':
    case 'statut':
      return <ContenuCellule col={col} issue={moinsAvancee(soeurs)} ctx={ctx} />
    case 'machine':
      return <span className="tableau-secondaire">{libelleMachines(soeurs.length)}</span>
    default:
      return valeurCommune(col, soeurs, ctx) ? (
        <ContenuCellule col={col} issue={issue} ctx={ctx} />
      ) : (
        <span className="tableau-secondaire">Plusieurs</span>
      )
  }
}

// ─── Titre de groupe ───

function LigneGroupe({
  mode,
  cle,
  items,
  ctx,
  replie,
  onBascule,
  onOuvrir,
}: {
  mode: Regroupement
  cle: string
  items: readonly Issue[]
  ctx: ContexteTableau
  replie: boolean
  onBascule: () => void
  onOuvrir: (chemin: string) => void
}): JSX.Element {
  const resume = resumerGroupe(items, ctx)
  const premier = items[0]
  const sansValeur = cle === SANS_VALEUR

  // Machine et projet ont une fiche ; métier et statut non : leur titre replie.
  let chemin: string | null = null
  if (mode === 'machine' && premier?.numeroSerie && !sansValeur) chemin = lienEquipement(premier.numeroSerie)
  if (mode === 'pn' && premier?.pn && !sansValeur) chemin = lienProjet(premier.pn)

  const nbMachines = `${resume.nbMachines} équipement${resume.nbMachines > 1 ? 's' : ''}`
  const nb = `${resume.nbProblemes} problème${resume.nbProblemes > 1 ? 's' : ''}`
  const etat =
    mode === 'statut'
      ? nb
      : resume.nbActifs > 0
        ? `${resume.nbActifs} actif${resume.nbActifs > 1 ? 's' : ''} sur ${nb}`
        : `${nb} · tous terminés`

  let titre: JSX.Element
  if (mode === 'machine') {
    titre = (
      <>
        <b className="tableau-groupe-nom">{sansValeur ? 'Sans équipement' : cle}</b>
        <span className="tableau-groupe-detail">{[resume.modele, resume.pn].filter(Boolean).join(' · ')}</span>
      </>
    )
  } else if (mode === 'pn') {
    titre = (
      <>
        <b className="tableau-groupe-nom">{sansValeur ? 'Sans projet' : cle}</b>
        <span className="tableau-groupe-detail">{nbMachines}</span>
      </>
    )
  } else if (mode === 'metier') {
    titre = (
      <>
        <b className="tableau-groupe-nom">
          <PastilleMetier metier={premier!.metier} />
        </b>
        <span className="tableau-groupe-detail">{nbMachines}</span>
      </>
    )
  } else {
    titre = (
      <>
        <b className="tableau-groupe-nom">
          <StatutLibelle statut={premier!.statut} />
        </b>
        <span className="tableau-groupe-detail">{nbMachines}</span>
      </>
    )
  }

  return (
    <div className="tableau-ligne tableau-ligne--groupe" role="row">
      <div className="tableau-groupe">
        <button
          type="button"
          className="tableau-groupe-chevron"
          aria-expanded={!replie}
          aria-label={`${replie ? 'Déplier' : 'Replier'} ${sansValeur ? 'ce groupe' : cle}`}
          onClick={onBascule}
        >
          {replie ? <ChevronRight size={17} /> : <ChevronDown size={17} />}
        </button>
        <button
          type="button"
          className={'tableau-groupe-titre' + (chemin ? ' tableau-groupe-titre--lien' : '')}
          title={chemin ? (mode === 'pn' ? 'Ouvrir la fiche projet' : 'Ouvrir la fiche équipement') : undefined}
          onClick={() => (chemin ? onOuvrir(chemin) : onBascule())}
        >
          {titre}
          {chemin && <ArrowUpRight size={14} className="tableau-groupe-lien" aria-hidden="true" />}
        </button>
        <span className="tableau-groupe-resume">
          {resume.criticiteMax && (
            <span className="tableau-groupe-criticite" title="Criticité la plus haute parmi les problèmes actifs">
              <CriticiteLibellee criticite={resume.criticiteMax} />
            </span>
          )}
          <span>{etat}</span>
          {resume.nbActifs > 0 && <span>plus ancien : {resume.plusAncienJours} j</span>}
        </span>
      </div>
    </div>
  )
}
